// ===============================================================
// file: Generators.js
// ===============================================================
// React component to list, search, and filter Generator locations stored in
//     /teams/{teamId}/generators/{doc-id}
//
// 🧩  THIS IS A MERGED VERSION  (ours + the user-supplied variant)  🧩
// ---------------------------------------------------------------------------
// • Authenticated users   →  view / search / filter / page
// • Admin UID list        →  future delete / edit hooks (place-holders below)
// • All styling uses Tailwind as per project convention.
//
// SEARCH + FILTERS
// ----------------
// • Full-text search box triggers TWO things:
//     1) A *reactive* array-contains lookup whenever the term is ≤20 chars and
//        single-token  →  catches docs beyond the local fetch limit.
//     2) A manual "Search" button / Enter key  →  always fires array-contains.
// • Filters:
//     • Type          (Broker / Direct / Internalized)
//     • State         (dropdown)
//     • City          (dropdown scoped by State)
//     • Broker        (dropdown)
// • Active filters render as inline "chips" for clarity.
//
// PAGINATION + LIMITS
// -------------------
// • Initial fetch capped at DEFAULT_FETCH_LIMIT (default 100) for snappy UX.
// • Pure client-side paging after that –  avoids extra Firestore reads.
//
// DEBUGGING
// ---------
// • Flip ENABLE_LOGGING=true to dump timing + query details to console.
// • All log calls routed through helper `log()` to silence easily.
//
// COMMENTS
// --------
// • Single-line // comments only (user preference).
// ===============================================================

import React, { useState, useEffect, useContext } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  FaSearch,
  FaSpinner,
  FaMapMarkerAlt,
  FaBuilding,
  FaFilter,
  FaEye,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

// ---------------------------------------------------------------------------
// CONSTANTS  (keep Admin list in sync with Firestore rules isAdmin())
// ---------------------------------------------------------------------------
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];
const TEAM_ID_FALLBACK = "zTS5IWYUGpICLRKioMPm"; // used if parent forgets to pass prop
const DEFAULT_FETCH_LIMIT = 100; // first pull capped – tweak as needed
const ENABLE_LOGGING = false; // flip true for verbose console output
const log = (...args) => ENABLE_LOGGING && console.log("[Generators]", ...args);

// ---------------------------------------------------------------------------
// Helper – colour-coded badge for type
// ---------------------------------------------------------------------------
const badgeClass = (type) => {
  switch (type) {
    case "Broker":
      return "bg-blue-100 text-blue-800";
    case "Direct":
      return "bg-green-100 text-green-800";
    case "Internalized":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// =============================================================
//                       MAIN COMPONENT
// =============================================================
const Generators = ({ teamId }) => {
  // prop fallback safety
  const activeTeamId = teamId || TEAM_ID_FALLBACK;
  if (!teamId) log("⚠️  teamId prop missing – falling back to constant");

  // auth context
  const { user } = useContext(AuthContext);
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  // core data state
  const [generators, setGenerators] = useState([]); // initial pull
  const [remoteDocs, setRemoteDocs] = useState([]); // array-contains hits
  const [filtered, setFiltered] = useState([]); // after search + filters

  // paging state
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  // search + filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterBroker, setFilterBroker] = useState("");

  // UX helpers
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState(null);

  // ---------------------------------------------
  // Firestore initial fetch (once on mount / auth)
  // ---------------------------------------------
  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const ref = collection(db, "teams", activeTeamId, "generators");
        const snap = await getDocs(
          query(ref, orderBy("customerName"), limit(DEFAULT_FETCH_LIMIT))
        );
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setGenerators(rows);
        setFiltered(rows);
        toast.success(`Loaded ${rows.length} generators`);
        log("Initial docs", rows.length);
      } catch (err) {
        console.error(err);
        toast.error("Load failed");
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTeamId]);

  // ------------------------------------------------------------------
  // Reactive remote search – fire for short single-token terms ≤20 chars
  // ------------------------------------------------------------------
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term || term.includes(" ") || term.length > 20) {
      setRemoteDocs([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const ref = collection(db, "teams", activeTeamId, "generators");
        const snap = await getDocs(
          query(ref, where("searchArray", "array-contains", term), limit(200))
        );
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        if (!cancelled) setRemoteDocs(rows);
        log(`Reactive search hits for '${term}':`, rows.length);
      } catch (e) {
        log("Reactive search failed", e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeTeamId]);

  // ---------------------------------------------
  // Manual search via button / Enter key
  // ---------------------------------------------
  const manualSearch = async () => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return toast.info("Enter a search term");
    setSearching(true);
    try {
      const ref = collection(db, "teams", activeTeamId, "generators");
      const snap = await getDocs(
        query(ref, where("searchArray", "array-contains", term), limit(500))
      );
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setRemoteDocs(rows); // override reactive docs with explicit results
      toast.info(`Found ${rows.length} remote hits`);
      log("Manual search hits", rows.length);
    } catch (err) {
      console.error(err);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // ---------------------------------------------
  // Build dropdown option lists (derived)
  // ---------------------------------------------
  const types = ["Broker", "Direct", "Internalized"];
  const states = Array.from(
    new Set(generators.map((g) => g.filters?.state).filter(Boolean))
  ).sort();
  const brokers = Array.from(
    new Set(generators.map((g) => g.brokerName).filter(Boolean))
  ).sort();
  const cities = Array.from(
    new Set(
      generators
        .filter((g) => !filterState || g.filters?.state === filterState)
        .map((g) => g.location?.city)
        .filter(Boolean)
    )
  ).sort();

  // ---------------------------------------------
  // Merge docs + apply filters whenever deps change
  // ---------------------------------------------
  useEffect(() => {
    let rows = [...generators, ...remoteDocs];
    const seen = new Set();
    rows = rows.filter((g) => {
      if (seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });

    // text search across several fields
    const term = searchTerm.toLowerCase();
    if (term) {
      rows = rows.filter((g) => {
        const city = g.location?.city || "";
        const state = g.location?.state || "";
        return (
          g.generatorName?.toLowerCase().includes(term) ||
          g.customerName?.toLowerCase().includes(term) ||
          g.brokerName?.toLowerCase().includes(term) ||
          city.toLowerCase().includes(term) ||
          state.toLowerCase().includes(term) ||
          g.location?.zipCode?.includes(term)
        );
      });
    }

    if (filterType) rows = rows.filter((g) => g.type === filterType);
    if (filterState)
      rows = rows.filter((g) => g.filters?.state === filterState);
    if (filterCity) rows = rows.filter((g) => g.location?.city === filterCity);
    if (filterBroker) rows = rows.filter((g) => g.brokerName === filterBroker);

    setFiltered(rows);
    setCurrentPage(1);
  }, [
    searchTerm,
    filterType,
    filterState,
    filterCity,
    filterBroker,
    generators,
    remoteDocs,
  ]);

  // paging helpers
  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const pageSlice = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const goTo = (p) => p >= 1 && p <= totalPages && setCurrentPage(p);
  const changePageSize = (val) => {
    setItemsPerPage(Number(val));
    setCurrentPage(1);
  };

  // modal open
  const openView = (g) => {
    setSelected(g);
    setShowView(true);
  };

  // clear filters helper
  const clear = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterState("");
    setFilterCity("");
    setFilterBroker("");
    setRemoteDocs([]);
    setCurrentPage(1);
  };

  // ===========================================================
  // RENDER
  // ===========================================================
  return (
    <div className="container mx-auto px-4 py-8">
      {/* header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl font-bold">Generator Sites</h1>
          <p className="text-gray-600">
            Search and filter waste generator locations
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Total: {filtered.length} of {generators.length}
        </div>
      </div>

      {/* search + filters card */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        {/* search row */}
        <div className="relative mb-4">
          <input
            className="w-full p-3 pl-10 pr-20 border rounded-md focus:ring-2 focus:ring-purple-500"
            placeholder="Search customer, city, state, ZIP…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") manualSearch();
            }}
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <button
            onClick={manualSearch}
            disabled={searching}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 flex items-center"
          >
            {searching ? <FaSpinner className="animate-spin" /> : "Search"}
          </button>
        </div>

        {/* dropdown filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="">All Types</option>
            {types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <select
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterCity("");
            }}
            className="p-2 border rounded-md"
          >
            <option value="">All States</option>
            {states.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="p-2 border rounded-md"
            disabled={!filterState && cities.length === 0}
          >
            <option value="">All Cities</option>
            {cities.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <select
            value={filterBroker}
            onChange={(e) => setFilterBroker(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="">All Brokers</option>
            {brokers.map((b) => (
              <option key={b}>{b}</option>
            ))}
          </select>
          <button
            onClick={clear}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* items-per-page + active chips */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <span>Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => changePageSize(e.target.value)}
            className="border rounded px-2 py-1"
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
          <span>per page</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {searchTerm && (
            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm">
              Search: "{searchTerm}"
            </span>
          )}
          {filterType && (
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
              Type: {filterType}
            </span>
          )}
          {filterState && (
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
              State: {filterState}
            </span>
          )}
          {filterCity && (
            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm">
              City: {filterCity}
            </span>
          )}
          {filterBroker && (
            <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm">
              Broker: {filterBroker}
            </span>
          )}
        </div>
      </div>

      {/* table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <FaSpinner className="animate-spin text-2xl text-purple-600 mx-auto mb-2" />
            <p>Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            <FaFilter className="text-4xl mx-auto mb-2 text-gray-400" />
            <p>No generators match</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "Type",
                    "Customer/Address",
                    "Generator",
                    "Location",
                    "Broker",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageSlice.map((g) => (
                  <tr key={g.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeClass(
                          g.type
                        )}`}
                      >
                        {g.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {g.customerName || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {g.generatorName || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-start space-x-1">
                        <FaMapMarkerAlt className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <div className="text-gray-900">
                            {g.location?.city || "—"},{" "}
                            {g.location?.state || "—"}{" "}
                            {g.location?.zipCode || ""}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {g.location?.street || ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {g.brokerName ? (
                        <div className="flex items-center space-x-1">
                          <FaBuilding className="text-gray-400" />
                          <span>{g.brokerName}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => openView(g)}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        <FaEye className="inline mr-1" />
                        View
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() =>
                            toast.info("Delete not implemented yet")
                          }
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrash className="inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* pagination */}
      {filtered.length > 0 && (
        <div className="mt-4 flex justify-center">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => goTo(1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              First
            </button>
            <button
              onClick={() => goTo(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Prev
            </button>
            <div className="mx-2 flex items-center">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) {
                  p = i + 1;
                } else {
                  const off = Math.min(
                    Math.max(0, currentPage - 3),
                    Math.max(0, totalPages - 5)
                  );
                  p = i + 1 + off;
                }
                return (
                  <button
                    key={p}
                    onClick={() => goTo(p)}
                    className={`w-8 h-8 mx-1 rounded-full ${
                      currentPage === p
                        ? "bg-purple-600 text-white"
                        : "bg-white hover:bg-gray-100 border"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => goTo(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1 border rounded ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => goTo(totalPages)}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1 border rounded ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* view modal */}
      {showView && selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* modal header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selected.customerName}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selected.generatorName}
                </p>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowView(false)}
              >
                <svg
                  className="w-6 h-6"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {/* modal body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* left */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Generator Info
                    </h3>
                    <div className="bg-gray-50 rounded p-3 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Type:</span>
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeClass(
                            selected.type
                          )}`}
                        >
                          {selected.type}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Customer:</span>
                        <span className="text-sm font-medium">
                          {selected.customerName || "—"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">
                          Generator:
                        </span>
                        <span className="text-sm font-medium">
                          {selected.generatorName || "—"}
                        </span>
                      </div>
                      {selected.brokerName && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Broker:</span>
                          <span className="text-sm font-medium">
                            {selected.brokerName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Location Details
                    </h3>
                    <div className="bg-gray-50 rounded p-3 space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">
                          Full Address:
                        </span>
                        <p className="text-sm font-medium mt-1 break-words">
                          {selected.location?.full ||
                            `${selected.location?.street || ""} ${
                              selected.location?.city || ""
                            }, ${selected.location?.state || ""} ${
                              selected.location?.zipCode || ""
                            }`.trim() ||
                            "—"}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <span className="text-xs text-gray-600">City:</span>
                          <p className="text-sm font-medium">
                            {selected.location?.city || "—"}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">State:</span>
                          <p className="text-sm font-medium">
                            {selected.location?.state || "—"}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">ZIP:</span>
                          <p className="text-sm font-medium">
                            {selected.location?.zipCode || "—"}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">
                            Confidence:
                          </span>
                          <p className="text-sm font-medium">
                            {selected.location?.confidence != null
                              ? `${Math.round(
                                  selected.location.confidence * 100
                                )}%`
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* right */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Raw Data
                    </h3>
                    <div className="bg-gray-50 rounded p-3 space-y-2 text-xs font-mono">
                      <div>
                        <span className="text-gray-600">Type:</span>{" "}
                        {selected.rawData?.type || "—"}
                      </div>
                      <div>
                        <span className="text-gray-600">Customer:</span>{" "}
                        {selected.rawData?.customer || "—"}
                      </div>
                      <div>
                        <span className="text-gray-600">Address:</span>
                        <p className="break-words mt-1">
                          {selected.rawData?.address || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Search Tokens ({selected.searchArray?.length || 0})
                      </h3>
                      <div className="bg-gray-50 rounded p-3 flex flex-wrap gap-1">
                        {selected.searchArray?.map((t, i) => (
                          <span
                            key={i}
                            className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs"
                          >
                            {t}
                          </span>
                        )) || (
                          <span className="text-sm text-gray-500">None</span>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Metadata
                    </h3>
                    <div className="bg-gray-50 rounded p-3 space-y-1 text-xs">
                      <div>
                        <span className="text-gray-600">Doc ID:</span>{" "}
                        {selected.id}
                      </div>
                      <div>
                        <span className="text-gray-600">Unique Key:</span>{" "}
                        {selected.metadata?.uniqueKey || "—"}
                      </div>
                      <div>
                        <span className="text-gray-600">Row #:</span>{" "}
                        {selected.metadata?.sourceRowNumber || "—"}
                      </div>
                      <div>
                        <span className="text-gray-600">Index:</span>{" "}
                        {selected.metadata?.locationIndex || 0} /{" "}
                        {selected.metadata?.totalLocations || 0}
                      </div>
                      <div>
                        <span className="text-gray-600">Imported:</span>{" "}
                        {selected.metadata?.importedAt || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* modal footer */}
            <div className="bg-gray-50 px-6 py-3 border-t flex justify-end"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Generators;
